/**
 * Bounded Context Analysis Service
 * 
 * Application service for analyzing bounded context patterns and compliance.
 * Provides meta-model analysis capabilities for architectural governance.
 * 
 * Following Eric Evans DDD Application Service patterns.
 */

import { injectable, inject } from 'inversify';
import { BoundedContextPattern, BoundedContextPatternData } from '../../domain/entities/BoundedContextPattern';
import { ArchitecturalAnalysis, ArchitecturalAnalysisData } from '../../domain/entities/ArchitecturalAnalysis';

// Port interfaces for infrastructure
export interface IBoundedContextPatternRepository {
  save(pattern: BoundedContextPattern): Promise<void>;
  findById(id: string): Promise<BoundedContextPattern | null>;
  findByPackageName(packageName: string): Promise<BoundedContextPattern | null>;
  findAll(): Promise<BoundedContextPattern[]>;
  findByType(contextType: 'core' | 'supporting' | 'generic'): Promise<BoundedContextPattern[]>;
}

export interface IArchitecturalAnalysisRepository {
  save(analysis: ArchitecturalAnalysis): Promise<void>;
  findById(id: string): Promise<ArchitecturalAnalysis | null>;
  findLatest(): Promise<ArchitecturalAnalysis | null>;
  findAll(): Promise<ArchitecturalAnalysis[]>;
}

export interface IPackageScanner {
  scanPackage(packagePath: string): Promise<{
    packageName: string;
    contextName: string;
    contextType: 'core' | 'supporting' | 'generic';
    modulePatternType: 'createXModule' | 'static-configure' | 'no-module';
    hasSymbolConstants: boolean;
    symbolConstantName?: string;
    architecturalPatterns: {
      hasCleanArchitecture: boolean;
      hasPortsAndAdapters: boolean;
      hasCQRS: boolean;
      hasEventSourcing: boolean;
      hasDomainEvents: boolean;
      hasAntiCorruptionLayer: boolean;
    };
    dependencies: string[];
  }>;
  
  scanMonorepo(monorepoPath: string): Promise<string[]>;
}

export interface IDependencyAnalyzer {
  analyzeDependencies(packages: string[]): Promise<{
    dependencyGraph: {
      nodes: Array<{ id: string; name: string; type: 'core' | 'supporting' | 'generic'; complianceScore: number; }>;
      edges: Array<{ source: string; target: string; type: 'depends-on' | 'publishes-to' | 'subscribes-to'; }>;
      cycles: string[][];
    };
  }>;
}

/**
 * Analysis Request DTOs
 */
export interface AnalyzeBoundedContextRequest {
  packagePath: string;
  forceRefresh?: boolean;
}

export interface AnalyzeMonorepoRequest {
  monorepoPath: string;
  analysisType: 'full' | 'incremental' | 'targeted';
  targetPackages?: string[];
}

export interface GetComplianceReportRequest {
  includeRecommendations?: boolean;
  filterByType?: 'core' | 'supporting' | 'generic';
}

/**
 * Response DTOs
 */
export interface BoundedContextAnalysisResult {
  pattern: BoundedContextPatternData;
  recommendations: string[];
  complianceScore: number;
}

export interface MonorepoAnalysisResult {
  analysis: ArchitecturalAnalysisData;
  summary: {
    overallHealth: number;
    totalPackages: number;
    compliantPackages: number;
    highPriorityIssues: number;
    circularDependencies: number;
    meetsStandards: boolean;
  };
}

export interface ComplianceReportResult {
  patterns: BoundedContextPatternData[];
  overallCompliance: number;
  patternDistribution: Record<string, number>;
  recommendations: Array<{
    priority: 'high' | 'medium' | 'low';
    category: string;
    description: string;
    affectedPackages: string[];
  }>;
}

/**
 * Bounded Context Analysis Service
 * 
 * Orchestrates meta-model analysis of bounded contexts and architectural patterns.
 */
@injectable()
export class BoundedContextAnalysisService {
  constructor(
    @inject('IBoundedContextPatternRepository')
    private boundedContextPatternRepository: IBoundedContextPatternRepository,
    
    @inject('IArchitecturalAnalysisRepository')
    private architecturalAnalysisRepository: IArchitecturalAnalysisRepository,
    
    @inject('IPackageScanner')
    private packageScanner: IPackageScanner,
    
    @inject('IDependencyAnalyzer')
    private dependencyAnalyzer: IDependencyAnalyzer
  ) {}

  /**
   * Analyze a single bounded context package
   */
  async analyzeBoundedContext(request: AnalyzeBoundedContextRequest): Promise<BoundedContextAnalysisResult> {
    const { packagePath, forceRefresh = false } = request;
    
    // Scan the package for patterns
    const scanResult = await this.packageScanner.scanPackage(packagePath);
    
    // Check if we have an existing analysis
    let pattern = await this.boundedContextPatternRepository.findByPackageName(scanResult.packageName);
    
    if (!pattern || forceRefresh) {
      // Analyze dependencies
      const depAnalysis = await this.dependencyAnalyzer.analyzeDependencies([scanResult.packageName]);
      
      const dependencyAnalysisData = {
        inboundDependencies: this.getInboundDependencies(scanResult.packageName, depAnalysis.dependencyGraph),
        outboundDependencies: scanResult.dependencies,
        circularDependencies: depAnalysis.dependencyGraph.cycles.find(cycle => 
          cycle.includes(scanResult.packageName)
        ) || [],
        violatesCleanArchitecture: this.checkCleanArchitectureViolations(scanResult.dependencies),
      };

      if (pattern) {
        // Update existing pattern
        pattern.updateAnalysis(
          scanResult.modulePatternType,
          scanResult.hasSymbolConstants,
          scanResult.symbolConstantName,
          scanResult.architecturalPatterns,
          dependencyAnalysisData
        );
      } else {
        // Create new pattern
        pattern = BoundedContextPattern.create({
          packageName: scanResult.packageName,
          contextName: scanResult.contextName,
          contextType: scanResult.contextType,
          modulePatternType: scanResult.modulePatternType,
          hasSymbolConstants: scanResult.hasSymbolConstants,
          symbolConstantName: scanResult.symbolConstantName,
          architecturalPatterns: scanResult.architecturalPatterns,
          dependencyAnalysis: dependencyAnalysisData,
          complianceScore: { overall: 0, modulePattern: 0, symbolUsage: 0, architecturalCompliance: 0, dependencyHealth: 0 },
          lastAnalyzedAt: new Date(),
        });
      }

      await this.boundedContextPatternRepository.save(pattern);
    }

    return {
      pattern: pattern.toData(),
      recommendations: pattern.getRecommendations(),
      complianceScore: pattern.complianceScore.overall,
    };
  }

  /**
   * Analyze entire monorepo architecture
   */
  async analyzeMonorepo(request: AnalyzeMonorepoRequest): Promise<MonorepoAnalysisResult> {
    const { monorepoPath, analysisType, targetPackages } = request;
    
    // Create analysis entity
    const analysis = ArchitecturalAnalysis.create(monorepoPath, analysisType);
    
    // Discover packages
    const allPackages = await this.packageScanner.scanMonorepo(monorepoPath);
    const packagesToAnalyze = targetPackages || allPackages;
    
    // Analyze each package
    const patterns: BoundedContextPattern[] = [];
    for (const packageName of packagesToAnalyze) {
      try {
        const result = await this.analyzeBoundedContext({ 
          packagePath: packageName, 
          forceRefresh: true 
        });
        const pattern = BoundedContextPattern.fromData(result.pattern);
        patterns.push(pattern);
      } catch (error) {
        console.warn(`Failed to analyze package ${packageName}:`, error);
      }
    }

    // Analyze dependencies
    const depAnalysis = await this.dependencyAnalyzer.analyzeDependencies(packagesToAnalyze);
    
    // Calculate monorepo health
    const compliantContexts = patterns.filter(p => p.isCompliant()).length;
    const totalComplianceScore = patterns.reduce((sum, p) => sum + p.complianceScore.overall, 0);
    
    const monorepoHealth = {
      totalPackages: patterns.length,
      totalBoundedContexts: patterns.length,
      compliantContexts,
      nonCompliantContexts: patterns.length - compliantContexts,
      averageComplianceScore: patterns.length > 0 ? totalComplianceScore / patterns.length : 0,
    };

    // Calculate pattern compliance
    const patternCompliance = {
      modulePatternCompliance: this.calculatePatternCompliance(patterns, 'modulePattern'),
      symbolUsageCompliance: this.calculatePatternCompliance(patterns, 'symbolUsage'),
      cleanArchitectureCompliance: this.calculatePatternCompliance(patterns, 'architecturalCompliance'),
      portsAdaptersCompliance: this.calculatePatternCompliance(patterns, 'dependencyHealth'),
    };

    // Generate recommendations
    const recommendations = this.generateRecommendations(patterns);

    // Complete analysis
    analysis.completeAnalysis(
      monorepoHealth,
      depAnalysis.dependencyGraph,
      patternCompliance,
      recommendations
    );

    await this.architecturalAnalysisRepository.save(analysis);

    return {
      analysis: analysis.toData(),
      summary: analysis.getSummary(),
    };
  }

  /**
   * Get compliance report
   */
  async getComplianceReport(request: GetComplianceReportRequest = {}): Promise<ComplianceReportResult> {
    const { includeRecommendations = true, filterByType } = request;
    
    let patterns = await this.boundedContextPatternRepository.findAll();
    
    if (filterByType) {
      patterns = patterns.filter(p => p.contextType === filterByType);
    }

    const patternData = patterns.map(p => p.toData());
    const overallCompliance = patterns.length > 0 
      ? patterns.reduce((sum, p) => sum + p.complianceScore.overall, 0) / patterns.length 
      : 0;

    const patternDistribution = patterns.reduce((dist, p) => {
      dist[p.modulePatternType] = (dist[p.modulePatternType] || 0) + 1;
      return dist;
    }, {} as Record<string, number>);

    const recommendations = includeRecommendations 
      ? this.generateRecommendations(patterns)
      : [];

    return {
      patterns: patternData,
      overallCompliance,
      patternDistribution,
      recommendations,
    };
  }

  // Helper methods
  private getInboundDependencies(packageName: string, graph: any): string[] {
    return graph.edges
      .filter((edge: any) => edge.target === packageName)
      .map((edge: any) => edge.source);
  }

  private checkCleanArchitectureViolations(dependencies: string[]): boolean {
    // Simplified check - in reality would analyze actual dependency directions
    return dependencies.some(dep => dep.includes('infrastructure') || dep.includes('ui'));
  }

  private calculatePatternCompliance(patterns: BoundedContextPattern[], scoreType: keyof BoundedContextPattern['complianceScore']): number {
    if (patterns.length === 0) return 0;
    return patterns.reduce((sum, p) => sum + p.complianceScore[scoreType], 0) / patterns.length;
  }

  private generateRecommendations(patterns: BoundedContextPattern[]): any[] {
    const recommendations: any[] = [];
    
    patterns.forEach(pattern => {
      const patternRecs = pattern.getRecommendations();
      patternRecs.forEach(rec => {
        recommendations.push({
          priority: pattern.complianceScore.overall < 50 ? 'high' : 'medium',
          category: 'module-pattern',
          description: rec,
          affectedPackages: [pattern.packageName],
        });
      });
    });

    return recommendations;
  }
}